import { apiError } from "../api/client";

/**
 * The free slots a `appointment_slot_conflict` 409 came back with, as one-click alternatives.
 *
 * <p>Exists because the old message said what was wrong ("khung giờ đã có lịch") but not what to do
 * next, so reception guessed another time and pressed again — 81 rejections in 40 minutes at one
 * clinic (#18672). The backend now walks forward and returns the doctor's next free starts; this
 * turns them into buttons.
 *
 * <p>Renders nothing unless the error really is a slot conflict with something useful to add, so it
 * is safe to drop next to any existing error line: a different failure keeps showing only its own
 * message.
 *
 * <p>`takenStarts` is the other half of #18681. The chips answer "where could this go instead";
 * they do not answer the question a receptionist filling a standing grid by hand actually has —
 * *"is this slot taken by someone else, or did I already enter it?"*. Listing the doctor's booked
 * starts turns the refusal into the grid they are trying to fill. Times only: no PHI.
 */
export function SlotConflictHint({
  error,
  onPick,
  takenStarts = [],
}: {
  error: unknown;
  /** Receives the chosen slot as the ISO-8601 instant the API returned. */
  onPick: (isoStart: string) => void;
  /** `HH:mm` starts this doctor is already booked for that day — see `takenStartsFor`. */
  takenStarts?: readonly string[];
}) {
  if (!error) return null;
  const problem = apiError(error);
  if (problem.code !== "appointment_slot_conflict") return null;

  const raw = (problem.problem as { suggestedSlots?: unknown } | null)?.suggestedSlots;
  const slots = Array.isArray(raw) ? raw.filter((s): s is string => typeof s === "string") : [];
  // A full day has no chips to offer, and that is precisely when the taken list carries the
  // information — so this no longer hides on an empty suggestion list alone.
  if (slots.length === 0 && takenStarts.length === 0) return null;

  return (
    <div className="mt-1.5 space-y-1">
      {takenStarts.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Bác sĩ đã có lịch lúc:</span>
          {takenStarts.map((hhmm) => (
            <span
              key={hhmm}
              className="rounded-md border border-amber-300 bg-amber-100/60 px-2 py-0.5 font-mono text-xs text-amber-900"
            >
              {hhmm}
            </span>
          ))}
        </div>
      )}
      {slots.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Giờ còn trống:</span>
          {slots.map((iso) => (
            <button
              key={iso}
              type="button"
              onClick={() => onPick(iso)}
              className="rounded-md border border-input bg-background px-2 py-0.5 font-mono text-xs hover:bg-accent"
            >
              {formatSlot(iso)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Whether a failed save was only a taken slot (#18787, #18681).
 *
 * <p>A taken slot is a normal outcome of hunting for a free time, so callers show it as
 * information (amber, `role="status"`) with the alternatives beside it. Every other failure keeps
 * the red `role="alert"` treatment.
 */
export function isSlotConflictError(error: unknown): boolean {
  return Boolean(error) && apiError(error).code === "appointment_slot_conflict";
}

/** `2026-09-02T09:30+07:00` → `09:30`, falling back to the raw value if it will not parse. */
function formatSlot(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
